export default function CVEditSupport() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Support - CVEdit
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Help, troubleshooting, and contact details for CVEdit.
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <h2 className="text-2xl font-bold text-[#A8720E]">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I write and run a C program?
              </h3>
              <p className="mt-1">
                Tap the + button to create a new file. Write your C code in
                the editor, then tap the Run button to compile and execute it.
                The output appears in the console panel beside or below the
                editor. Compilation and execution happen entirely on your
                device using the built-in C compiler — no internet connection
                is required to run code.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I send input to a program that uses{" "}
                <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">
                  scanf
                </code>
                ?
              </h3>
              <p className="mt-1">
                Before you tap Run, open the input panel and paste or type
                what your program should read from standard input. When the
                program reads, it will consume from that buffer. You can keep
                the input panel pinned open while you iterate.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                What is the Problem of the Day?
              </h3>
              <p className="mt-1">
                A short practice problem is picked daily from the bundled
                problem bank. It appears on the home screen with a prompt,
                starter code, and test cases. Solve it, run the tests, and
                move on — or skip to the Practice tab to pick a problem by
                topic and difficulty.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do the practice problems work?
              </h3>
              <p className="mt-1">
                Each problem ships with a prompt, starter code, and two or
                more test cases. When you tap Run Tests, CVEdit compiles your
                program on-device, feeds each test case to it as standard
                input, and compares your program&apos;s output with the
                expected output. A pass/fail summary is shown. Problems are
                grouped by topic (arrays, strings, linked lists, trees,
                recursion, pointers, and more) and by difficulty.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                What does the memory visualizer do?
              </h3>
              <p className="mt-1">
                The memory visualizer draws a simple picture of variables,
                addresses, and pointer targets so you can see how{" "}
                <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">
                  int *p = &amp;x
                </code>
                ,{" "}
                <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">
                  malloc
                </code>
                ,{" "}
                <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">
                  free
                </code>
                , and array indexing behave. Use the step slider to scrub
                through the ops one at a time.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Why do compiler errors look different from my desktop
                compiler?
              </h3>
              <p className="mt-1">
                CVEdit shows the raw compiler output, then adds a
                plain-English explanation below each error where a common
                pattern is recognised (missing{" "}
                <code className="rounded bg-[#F1EEE5] px-1 text-[#A8720E]">
                  ;
                </code>
                , undeclared variable, conflicting type, pointer-integer
                mismatch, and so on). The idea is to help learners understand
                what the compiler is trying to tell them without leaving the
                app.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Does CVEdit support C++?
              </h3>
              <p className="mt-1">
                Yes. CVEdit supports both C and C++ on-device. Tap the C / C++
                toggle in the editor toolbar to switch, or set a default
                language in Settings &gt; Default language.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I save my work?
              </h3>
              <p className="mt-1">
                Tap Save in the editor menu to keep a file in the app&apos;s
                local library. Recent files also appear on the home screen so
                you can jump back in quickly. Saved files live on your device
                and are not uploaded anywhere.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Why does the app show ads?
              </h3>
              <p className="mt-1">
                Ads help support continued development. You can remove all
                ads with an optional one-time in-app purchase from Settings.
                No subscriptions.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                My program hangs when I tap Run. What should I do?
              </h3>
              <p className="mt-1">
                Tap Stop to cancel execution. Check for infinite loops, or
                for a read from standard input when the input panel is empty
                (the program will wait forever). If the editor still feels
                stuck, close and reopen the file.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                The app is crashing. What do I do?
              </h3>
              <p className="mt-1">
                Close and reopen the app. Make sure you have the latest
                version from Google Play. If a specific program causes the
                crash, look for out-of-bounds writes, use-after-free, or very
                large allocations. Email us with your device model, OS
                version, app version, and steps to reproduce.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Contact Support
          </h2>
          <p>
            For help, bug reports, or privacy-related questions, email{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            . We usually respond within 24-48 hours.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Report a Bug</h2>
          <p>
            If something is not working as expected, include these details in
            your email:
          </p>
          <ul className="list-disc space-y-1 pl-6">
            <li>Your device model and Android OS version</li>
            <li>Your app version (found in Settings &gt; About)</li>
            <li>What you were trying to do</li>
            <li>The error or unexpected result</li>
            <li>Steps to reproduce the issue, if known</li>
            <li>
              If it is a compiler error question, the shortest C program
              that reproduces it
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Important Links
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              Privacy Policy:{" "}
              <a
                href="/apps/cvedit/privacy-policy"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/cvedit/privacy-policy
              </a>
            </li>
            <li>
              Support:{" "}
              <a
                href="/apps/cvedit/support"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/cvedit/support
              </a>
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            App Information
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              <strong>App Name:</strong> CVEdit
            </li>
            <li>
              <strong>Package:</strong> com.cosmovex.cvedit
            </li>
            <li>
              <strong>Developer:</strong> Cosmovex
            </li>
            <li>
              <strong>Contact:</strong>{" "}
              <a
                href="mailto:trionapps@gmail.com"
                className="text-[#0F6BBF] underline"
              >
                trionapps@gmail.com
              </a>
            </li>
          </ul>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
